import { Trans, useTranslation } from 'react-i18next';

import {
    Body1,
    Body2,
    Button,
    Flex,
    Heading3,
    Heading4,
    IconAccountBalanceWallet,
    IconTile,
    List,
    ListItem,
    OutlinedCard,
    Stack,
    type IconName,
    getIconByName,
} from '@myphx-engineering/phx-design-system';
import type { SemanticColorVariants } from '@myphx-engineering/phx-design-system/framework';

import {
    useFillPathway,
    useFillPrescription,
} from '../../../../contexts/fill/use-fill-prescription';
import type { PrescriptionOffer } from '../../../../hooks/use-prescription-offers';
import { useProgramTerms } from '../../../../hooks/use-program-terms';
import { getAbsoluteRoute } from '../../../../util/get-absolute-route';

export type SelectInsurancePathCardProps = {
    offer: PrescriptionOffer | undefined;
};

const CardSubHeading = () => {
    const { t } = useTranslation('lilly');
    const { medication } = useFillPrescription();

    const terms = useProgramTerms(medication, 'self-pay');

    if (terms) {
        return t('fill.pathSelectV2.cards.selfPay.subHeadingWithCard');
    }

    return null;
};

const SelfPayCardBullets = () => {
    const { t } = useTranslation('lilly');

    const bullets = t('fill.pathSelectV2.cards.selfPay.bullets', {
        returnObjects: true,
    }) as Array<{
        color: SemanticColorVariants;
        icon: IconName;
        key: string;
        text: string;
    }>;

    return (
        <List>
            {bullets.map((bullet) => {
                const Icon = getIconByName(bullet.icon);

                return (
                    <ListItem
                        key={bullet.key}
                        icon={
                            <IconTile
                                color={bullet.color}
                                shape="circle"
                                size="xs"
                                Icon={Icon}
                            />
                        }
                    >
                        <Body1>
                            <Trans
                                t={t}
                                ns="dynamic"
                                components={{ b: <strong /> }}
                            >
                                {bullet.text}
                            </Trans>
                        </Body1>
                    </ListItem>
                );
            })}
        </List>
    );
};

export const SelectSelfPayPathCardV2 = ({
    offer,
}: SelectInsurancePathCardProps) => {
    const { t } = useTranslation('lilly');
    const { drxId } = useFillPrescription();
    const { originalPathway } = useFillPathway();

    const line1 =
        !offer || offer.headlinePriceType === 'SelfPayPriceAtPharmacy'
            ? t('fill.pathSelectV2.cards.priceAtPharmacy')
            : t('fill.pathSelectV2.cards.payAsLow');
    const line2 =
        !offer || offer.headlinePriceType === 'SelfPayPriceAtPharmacy'
            ? null
            : t('fill.pathSelectV2.cards.price', {
                  price: offer.patientCost,
              });
    const hasSavingsCard =
        offer?.headlinePriceType === 'CashAssistance' ||
        offer?.headlinePriceType === 'Incentive';

    return (
        <OutlinedCard
            pinFooter
            fullHeight
            data-testid="path-select-card-self-pay"
            header={
                <Flex gap="sm" data-testid="path-select-card-self-pay-title">
                    <IconTile
                        shape="squircle"
                        size="xs"
                        Icon={IconAccountBalanceWallet}
                    />
                    <Stack gap="xxxs" flex={1}>
                        <Heading3>
                            {t('fill.pathSelectV2.cards.selfPay.heading')}
                        </Heading3>
                        <Body2>
                            <CardSubHeading />
                        </Body2>
                    </Stack>
                </Flex>
            }
            footer={
                <Button
                    fullWidth
                    link
                    mt="md"
                    to={{
                        pathname: getAbsoluteRoute('fill.drxId.root', {
                            drxId,
                        }),
                        search: `?pathway=self-pay${originalPathway ? `&original-pathway=${originalPathway}` : ''}`,
                    }}
                >
                    {t('fill.pathSelectV2.cards.selfPay.button')}
                </Button>
            }
        >
            <Stack gap="md">
                <Stack gap="xxxs">
                    <Body1>{line1}</Body1>
                    {line2 ? <Heading4 size="2rem">{line2}</Heading4> : null}
                    {hasSavingsCard ? (
                        <Body2>
                            <Trans
                                t={t}
                                i18nKey="fill.pathSelectV2.cards.selfPay.withCardProgram"
                                components={{ b: <strong /> }}
                            />
                        </Body2>
                    ) : null}
                </Stack>
                <SelfPayCardBullets />
            </Stack>
        </OutlinedCard>
    );
};
